import { FormEvent, PropsWithChildren, useRef, useState } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  chakra,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Stack,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';

import {
  enterpriseResourceContentTypes,
  enterpriseResourceIndustries,
  enterpriseResourceKnowledgeAreas,
} from '../data';

import { ResourceFilterChips } from './resource-filter-chips';

export type Industry = (typeof enterpriseResourceIndustries)[number]['value'];
export type KnowledgeArea = (typeof enterpriseResourceKnowledgeAreas)[number]['value'];
export type ContentType = (typeof enterpriseResourceContentTypes)[number]['value'];

export type Filters = {
  industries: Industry[];
  knowledgeAreas: KnowledgeArea[];
  contentTypes: ContentType[];
};

export function ResourceCenterFilters({
  numberOfResults,
  onClearFilters,
  onFiltersChange,
}: {
  numberOfResults: number;
  onClearFilters: () => void;
  onFiltersChange: (filters: Filters) => void;
}) {
  const { isOpen: isFilterDrawOpen, onOpen: openFilterDrawer, onClose: closeFilterDrawer } = useDisclosure();
  const [selectedFilters, setSelectedFilters] = useState<Filters>({
    industries: [],
    knowledgeAreas: [],
    contentTypes: [],
  });

  const numberOfSelectedFilters =
    selectedFilters.industries.length + selectedFilters.knowledgeAreas.length + selectedFilters.contentTypes.length;

  function handleSelectedFiltersChange(
    filterType: keyof Filters,
    checked: boolean,
    value: Industry | KnowledgeArea | ContentType
  ) {
    let newSelectedFilters = [...selectedFilters[filterType]];

    if (checked) {
      newSelectedFilters.push(value);
    } else {
      const removedItemIndex = newSelectedFilters.indexOf(value);
      newSelectedFilters.splice(removedItemIndex, 1);
    }

    setSelectedFilters({ ...selectedFilters, [filterType]: newSelectedFilters });

    onFiltersChange({ ...selectedFilters, [filterType]: newSelectedFilters });
  }

  function handleFiltersClearClick() {
    onClearFilters();
    setSelectedFilters({
      industries: [],
      knowledgeAreas: [],
      contentTypes: [],
    });
  }

  return (
    <Box marginBottom='24px' marginTop={{ base: '40px', lg: '80px' }}>
      <Flex
        display={{ base: 'none', lg: 'flex' }}
        gap='8px'
        height='66px'
        justifyContent='flex-start'
        position='relative'
        zIndex={99}
      >
        <Box alignItems='center' display='flex' gap='16px' position='absolute' top='0'>
          <Text fontWeight='600'>Filter By</Text>

          <FilterForm selectedFilters={selectedFilters} onFiltersChange={handleSelectedFiltersChange} />
        </Box>
      </Flex>
      <Box display={{ base: 'none', lg: 'flex' }} justifyContent='flex-start' marginTop='24px'>
        {numberOfSelectedFilters > 0 && (
          <ResourceFilterChips selectedFilters={selectedFilters} onFilterRemove={handleSelectedFiltersChange} />
        )}
      </Box>
      <Box display={{ base: 'block', lg: 'none' }}>
        <Button variant='outline' onClick={openFilterDrawer}>
          Filter & Sort {Boolean(numberOfSelectedFilters) && `(${numberOfSelectedFilters})`}
        </Button>
      </Box>
      <Drawer isOpen={isFilterDrawOpen} placement='right' size='full' variant='mobile' onClose={closeFilterDrawer}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader alignItems='center' borderBottomWidth='1px' display='flex' justifyContent='space-between'>
            <Text flex={1} size='label' textAlign='center'>
              Filter & Sort
            </Text>
            <DrawerCloseButton />
          </DrawerHeader>
          <DrawerBody padding='0px 0px 80px'>
            <FilterForm selectedFilters={selectedFilters} isMobile onFiltersChange={handleSelectedFiltersChange} />
          </DrawerBody>
          <DrawerFooter backgroundColor='white' bottom='0px' gap='16px' left='0px' position='fixed' right='0px'>
            <Box flex={1} flexShrink={0} width='100%'>
              <Button variant='outline' width='100%' onClick={handleFiltersClearClick}>
                Clear ({numberOfSelectedFilters})
              </Button>
            </Box>
            <Box flex={1} flexShrink={0} width='100%'>
              <Button isLoading={!Boolean(numberOfResults)} width='100%' onClick={closeFilterDrawer}>
                {numberOfResults} Results
              </Button>
            </Box>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

export function FilterForm({
  selectedFilters,
  isMobile,
  onFiltersChange,
}: {
  selectedFilters: Filters;
  isMobile?: boolean;
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: Industry | KnowledgeArea | ContentType) => void;
}) {
  return (
    <chakra.form>
      <Flex
        flexDirection={isMobile ? 'column' : 'row'}
        gap={{ base: '0', lg: '16px' }}
        height={isMobile ? 'auto' : '66px'}
      >
        <FilterGroup isMobile={isMobile} numberOfSelectedFilters={selectedFilters.industries.length} title='Industry'>
          <IndustryFilter selectedIndustries={selectedFilters.industries} onFiltersChange={onFiltersChange} />
        </FilterGroup>

        <FilterGroup
          isMobile={isMobile}
          numberOfSelectedFilters={selectedFilters.knowledgeAreas.length}
          title='Knowledge Area'
        >
          <KnowledgeAreaFilter
            selectedKnowledgeAreas={selectedFilters.knowledgeAreas}
            onFiltersChange={onFiltersChange}
          />
        </FilterGroup>

        <FilterGroup
          isMobile={isMobile}
          numberOfSelectedFilters={selectedFilters.contentTypes.length}
          title='Content Type'
        >
          <ContentTypeFilter selectedContentTypes={selectedFilters.contentTypes} onFiltersChange={onFiltersChange} />
        </FilterGroup>
      </Flex>
    </chakra.form>
  );
}

export function FilterGroup({
  title,
  numberOfSelectedFilters,
  isMobile,
  defaultOpen,
  children,
}: PropsWithChildren<{
  title?: string;
  numberOfSelectedFilters?: number;
  isMobile?: boolean;
  defaultOpen?: boolean;
}>) {
  const [accordionIndex, setAccordionIndex] = useState<number>(isMobile || defaultOpen ? 0 : -1);
  const accordionRef = useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref: accordionRef,
    handler: () => !isMobile && setAccordionIndex(-1),
  });

  return (
    <Accordion
      ref={accordionRef}
      index={accordionIndex}
      width={isMobile ? '100%' : '256px'}
      allowToggle
      onChange={(index) => {
        typeof index === 'number' && setAccordionIndex(index);
      }}
    >
      <AccordionItem>
        <AccordionButton borderTop={{ base: 'none', lg: '1px' }} p={{ base: '8px 16px', lg: '16px' }}>
          <Box as='span' flex='1' textAlign='left'>
            {title} {Boolean(numberOfSelectedFilters) && `(${numberOfSelectedFilters})`}
          </Box>
          <AccordionIcon />
        </AccordionButton>

        <AccordionPanel p={{ base: '8px 16px 16px 16px', lg: '16px' }} sx={{ div: { position: 'relative' } }}>
          {children}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

export function IndustryFilter({
  selectedIndustries,
  onFiltersChange,
}: {
  selectedIndustries: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: Industry | KnowledgeArea | ContentType) => void;
}) {
  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('industries', checked, value as Industry);
  }

  return (
    <Stack spacing={4}>
      {enterpriseResourceIndustries.map((industry) => (
        <Checkbox
          key={industry.value}
          isChecked={selectedIndustries?.includes(industry.value)}
          name={`industry--${industry}`}
          size={{ base: 'lg', lg: 'md' }}
          value={industry.value}
          onChange={handleFiltersChange}
        >
          {industry.title}
        </Checkbox>
      ))}
    </Stack>
  );
}

export function KnowledgeAreaFilter({
  selectedKnowledgeAreas,
  onFiltersChange,
}: {
  selectedKnowledgeAreas: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: Industry | KnowledgeArea | ContentType) => void;
}) {
  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('knowledgeAreas', checked, value as KnowledgeArea);
  }

  return (
    <Stack spacing={4}>
      {enterpriseResourceKnowledgeAreas.map((knowledgeArea) => (
        <Checkbox
          key={knowledgeArea.value}
          isChecked={selectedKnowledgeAreas?.includes(knowledgeArea.value)}
          name={`knowledgeArea--${knowledgeArea}`}
          size={{ base: 'lg', lg: 'md' }}
          value={knowledgeArea.value}
          onChange={handleFiltersChange}
        >
          {knowledgeArea.title}
        </Checkbox>
      ))}
    </Stack>
  );
}

export function ContentTypeFilter({
  selectedContentTypes,
  onFiltersChange,
}: {
  selectedContentTypes: string[];
  onFiltersChange: (filterType: keyof Filters, checked: boolean, value: Industry | KnowledgeArea | ContentType) => void;
}) {
  function handleFiltersChange(event: FormEvent<HTMLInputElement>) {
    const { checked, value } = event.currentTarget;
    onFiltersChange('contentTypes', checked, value as ContentType);
  }

  return (
    <Stack spacing={4}>
      {enterpriseResourceContentTypes.map((contentType) => (
        <Checkbox
          key={contentType.value}
          isChecked={selectedContentTypes?.includes(contentType.value)}
          name={`contentType--${contentType}`}
          size={{ base: 'lg', lg: 'md' }}
          value={contentType.value}
          onChange={handleFiltersChange}
        >
          {contentType.title}
        </Checkbox>
      ))}
    </Stack>
  );
}
